import React, { useEffect, useRef, useState } from "react";
import { Button } from "../ui/Button";
import { Paperclip, X } from "lucide-react";

// Modal de encerramento: mensagem opcional + anexo opcional + e-mails em cópia.
// Ao confirmar, o chamado é encerrado e o e-mail de encerramento (com o anexo
// e a pesquisa de satisfação) é enviado ao solicitante e aos e-mails em CC.
const CloseTicketModal = ({ isOpen, onClose, ticket, onSubmit }) => {
  const [comment, setComment] = useState("");
  const [ccEmails, setCcEmails] = useState("");
  const [replyAll, setReplyAll] = useState(true);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const participants = (ticket?.participant_emails || "").trim();

  useEffect(() => {
    if (isOpen) {
      setComment("");
      // Padrão = responder a Todos: já preenche o CC com quem estava no Para/CC.
      setReplyAll(true);
      setCcEmails(ticket?.participant_emails || "");
      setFile(null);
      setSaving(false);
    }
  }, [isOpen, ticket]);

  if (!isOpen) return null;

  const handleConfirm = async () => {
    setSaving(true);
    try {
      await onSubmit({ comment: comment.trim(), cc_emails: ccEmails.trim(), file });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-xl max-h-[92vh] overflow-y-auto rounded-xl bg-white p-6 shadow-xl">
        <div className="flex justify-between items-center mb-1">
          <h2 className="text-lg font-semibold text-gray-900">Encerrar chamado #{ticket?.id}</h2>
          <Button variant="ghost" size="icon" onClick={onClose} aria-label="Fechar">✕</Button>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          A mensagem e o anexo serão enviados ao solicitante no e-mail de encerramento e ficam no histórico do chamado.
        </p>

        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-700">Mensagem de encerramento (opcional)</label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={4}
              placeholder="Descreva a solução, oriente o solicitante, etc."
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {participants && (
            <div>
              <label className="text-sm font-medium text-gray-700">Responder para</label>
              <div className="mt-1.5 flex flex-col gap-2 sm:flex-row sm:gap-5">
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="radio"
                    name="reply-scope"
                    checked={replyAll}
                    onChange={() => { setReplyAll(true); setCcEmails(participants); }}
                  />
                  Todos <span className="text-gray-400">(solicitante + Para/CC)</span>
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="radio"
                    name="reply-scope"
                    checked={!replyAll}
                    onChange={() => { setReplyAll(false); setCcEmails(""); }}
                  />
                  Somente o solicitante
                </label>
              </div>
              <p className="mt-1 text-xs text-gray-400 break-words">
                No <strong>Para/CC</strong> do e-mail que abriu o chamado: {participants}
              </p>
            </div>
          )}

          <div>
            <label className="text-sm font-medium text-gray-700">
              {participants ? "Cópia (CC) — ajuste se quiser" : "Copiar mais pessoas (CC) — opcional"}
            </label>
            <input
              type="text"
              value={ccEmails}
              onChange={(e) => setCcEmails(e.target.value)}
              placeholder="email1@empresa.com, email2@empresa.com"
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="mt-1 text-xs text-gray-400">Separe por vírgula. Todos recebem uma cópia do e-mail de encerramento.</p>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">Anexo (opcional)</label>
            <input ref={fileRef} type="file" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
            {!file ? (
              <div className="mt-1">
                <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} className="h-auto gap-2 px-3 py-2 text-gray-700">
                  <Paperclip size={16} /> Selecionar arquivo
                </Button>
              </div>
            ) : (
              <div className="mt-1 flex items-center justify-between rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm">
                <span className="truncate text-gray-700">{file.name}</span>
                <button type="button" onClick={() => setFile(null)} aria-label="Remover anexo" className="ml-2 text-gray-400 hover:text-gray-600">
                  <X size={16} />
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancelar</Button>
          <Button onClick={handleConfirm} disabled={saving}>
            {saving ? "Encerrando..." : "Encerrar chamado"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CloseTicketModal;
